import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import ChippedMultiSelect from 'components/shared/ChippedMultiSelect';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { ATTACHMENTS_VALUE_NAME, WorkflowAction } from 'models/Workflow';
import { GroupAccessMode, groupsQueryKeys, indexGroups } from 'services/api/group';
import { createWorkflow, updateWorkflow, workflowQueryKeys, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

import {
  DEMOGRAPHIC_FIELDS,
  INSURANCE_FIELDS,
  REFERRAL_INTAKE_API_NAME,
  ReferralFieldDef,
  VALUE_NAMES,
} from './constants';
import { RestartConsentStep } from './RestartConsentModal';

// Which section the modal opens focused on. 'demographics' expands the demographics
// section (the "Edit demographics" entry point); 'documents' is the default packet view
// (the "Add additional documents" entry point). On create the demographics section is
// collapsed unless explicitly opened.
export type ReferralModalSection = 'documents' | 'demographics';

interface Props {
  // Present in edit mode; absent when creating.
  workflow?: Workflow;
  section?: ReferralModalSection;
  onClose: () => void;
}

type BuildingOption = { id: string; name: string };

// Every value field across both sections; the date field is handled separately (dob state),
// the rest (text + select) share the `text` record.
const VALUE_FIELDS = [...DEMOGRAPHIC_FIELDS, ...INSURANCE_FIELDS];
const TEXT_FIELDS = VALUE_FIELDS.filter((field) => field.type !== 'date');

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

export default function ReferralModal({ workflow, section = 'documents', onClose }: Props) {
  const navigate = useNavigate();
  const isEdit = !!workflow;

  const [selectedBuildings, setSelectedBuildings] = useState<BuildingOption[]>(
    workflow?.groups.map((group) => ({ id: group.id, name: group.name })) ?? []
  );

  const stored = useMemo(() => {
    const text = Object.fromEntries(
      TEXT_FIELDS.map((field) => [field.name, workflow?.valueFor(field.name)?.value ?? ''])
    );
    const dob = workflow?.valueFor(VALUE_NAMES.PATIENT_DOB)?.value ?? '';
    return { text, dob };
  }, [workflow]);
  const originalGroupIds = useMemo(() => workflow?.groups.map((group) => group.id) ?? [], [workflow]);

  const [text, setText] = useState<Record<string, string>>(stored.text);
  const [dob, setDob] = useState<Date | null>(parseDate(stored.dob) ?? null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // The impact list awaiting the user's consent (design §9) — non-null while the modal
  // is on its second (confirm) step. Going back clears it without saving; the form's
  // state lives here in the parent, so the person's edits are preserved.
  const [pendingRestart, setPendingRestart] = useState<WorkflowAction[] | null>(null);
  // Demographics start expanded in edit mode or when the modal was opened to edit them;
  // collapsed on a fresh create (upload is the primary path).
  const [demographicsOpen, setDemographicsOpen] = useState(isEdit || section === 'demographics');

  const groupsParams = useMemo(
    () => ({ accessModes: [GroupAccessMode.Allowed], type: SKILLED_NURSING_FACILITY, pageSize: 200, sortBy: 'name' }),
    []
  );
  const { data: groupsResult, isPending: groupsLoading } = useQuery({
    queryKey: [...groupsQueryKeys.index, groupsParams],
    queryFn: ({ signal }) => indexGroups(groupsParams, signal),
  });
  const groups = groupsResult?.data ?? [];
  // With access to exactly one building there's nothing to choose — assume it and hide the
  // selector. The selector only appears when a user can address more than one building.
  const onlyBuilding = !groupsLoading && groups.length === 1 ? groups[0] : null;

  const queryClient = useQueryClient();

  // Apply the settled response to the cache so the acting user sees their own change
  // immediately, rather than waiting for the next list/detail poll to reconcile it.
  const cacheWorkflow = (saved: Workflow) => {
    queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.list });
  };

  const { mutate: create, isPending: creating } = useMutateWithToast({
    mutationFn: createWorkflow,
    onSuccess: (created: Workflow) => {
      cacheWorkflow(created);
      onClose();
      navigate(`/inbound-referrals/${created.id}`);
    },
  });
  const { mutate: save, isPending: saving } = useMutateWithToast({
    mutationFn: updateWorkflow,
    onSuccess: (updated: Workflow) => {
      cacheWorkflow(updated);
      onClose();
    },
  });

  const changedValues = (): WorkflowValuesInput => {
    const changes: WorkflowValuesInput = {};
    TEXT_FIELDS.forEach((field) => {
      const next = text[field.name].trim();
      if (next !== stored.text[field.name]) changes[field.name] = next;
    });
    const nextDob = simpleDashDate(dob) ?? '';
    if (nextDob !== stored.dob) changes[VALUE_NAMES.PATIENT_DOB] = nextDob;
    return changes;
  };

  const effectiveBuildings = onlyBuilding ? [{ id: onlyBuilding.id, name: onlyBuilding.name }] : selectedBuildings;
  const groupIds = effectiveBuildings.map((building) => building.id);
  const groupsChanged = isEdit && !sameIds(groupIds, originalGroupIds);
  const pendingValues = changedValues();
  const attachments = uploads.length > 0 ? uploads : undefined;
  const hasContent = Object.keys(pendingValues).length > 0 || !!attachments;
  const canSubmit = groupIds.length > 0 && (isEdit ? hasContent || groupsChanged : hasContent);

  // The steps this save would ripple into (design §9): the changed value names — a
  // packet upload is a change to the `attachments` value — walked through the declared
  // contracts. Empty on create and on terminal runs (the API rejects restarts there).
  const impactedActions = (): WorkflowAction[] => {
    if (!isEdit || workflow.isTerminal) return [];
    const changedNames = Object.keys(pendingValues);
    if (attachments) changedNames.push(ATTACHMENTS_VALUE_NAME);
    return changedNames.length > 0 ? workflow.impactedActionsBy(changedNames) : [];
  };

  const saveEdit = (restartActionIds?: string[]) => {
    if (!workflow) return;
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    save({
      workflowId: workflow.id,
      groupIds: groupsChanged ? groupIds : undefined,
      values,
      attachments,
      restartActionIds,
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    if (isEdit) {
      // Consent-at-save (design §9, 002 §6): when the correction ripples into steps
      // that already ran, the person approves the restart list before anything saves.
      const impacted = impactedActions();
      if (impacted.length > 0) {
        setPendingRestart(impacted);
        return;
      }
      saveEdit();
      return;
    }
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    create({ workflowDefinitionApiName: REFERRAL_INTAKE_API_NAME, groupIds, values, attachments });
  };

  const submitting = creating || saving;

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        {field.optional && <Optional> (optional)</Optional>}
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`field-${field.name}`}>
          <Select
            inputId={`field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`field-${field.name}`}
        />
      )}
    </Field>
  );

  // Two-step wizard, one modal (no modal-on-modal): step 1 is the edit form; when the
  // save ripples into started steps, step 2 swaps the modal's content to the restart
  // consent. The form stays mounted (hidden) so Back restores the person's edits —
  // including the uncontrolled file input's queued uploads.
  const title = pendingRestart ? 'Restart affected steps?' : isEdit ? 'Edit Referral' : 'Create New Referral';

  return (
    <StandardModal title={title} onCancel={onClose} maxWidth='660px'>
      {pendingRestart && (
        <RestartConsentStep
          actions={pendingRestart}
          saving={saving}
          onConfirm={() => saveEdit(pendingRestart.map((action) => action.id))}
          onBack={() => setPendingRestart(null)}
        />
      )}
      <Form $hidden={!!pendingRestart}>
        {groups.length > 1 && (
          <Field>
            <div data-testid='building-select'>
              <ChippedMultiSelect<BuildingOption>
                options={groups}
                value={selectedBuildings}
                onChange={setSelectedBuildings}
                getOptionValue={(building) => building.id}
                getOptionLabel={(building) => building.name}
                inputLabel='Buildings'
                containerLabel='Selected buildings'
                placeholder='Search buildings'
                closeMenuOnSelect={false}
              />
            </div>
          </Field>
        )}
        {!groupsLoading && groups.length === 0 && (
          <HelpText>You don’t have access to any buildings for inbound referrals.</HelpText>
        )}

        <div>
          <Lead>
            <strong>Upload any associated referral files below.</strong> Olio will process and extract patient
            demographics and related referral information.
          </Lead>
          {workflow && workflow.attachments.length > 0 && (
            <CurrentPacket data-testid='current-packet'>
              Current packet:{' '}
              {workflow.attachments
                .map((attachment) => attachment.filename)
                .filter(Boolean)
                .join(', ')}
            </CurrentPacket>
          )}
          <DropZone data-testid='packet-dropzone'>
            <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
          </DropZone>
          {workflow && uploads.length > 0 && workflow.attachments.length > 0 && (
            <ReplaceWarning data-testid='packet-replace-warning'>
              Uploading will replace the current {workflow.attachments.length} document
              {workflow.attachments.length > 1 ? 's' : ''}.
            </ReplaceWarning>
          )}
        </div>

        <div>
          <Toggle
            type='button'
            onClick={() => setDemographicsOpen((open) => !open)}
            aria-expanded={demographicsOpen}
            data-testid='demographics-toggle'>
            ✎ Or manually add patient demographics {demographicsOpen ? '▴' : '▾'}
          </Toggle>
          {demographicsOpen && (
            <div data-testid='demographics-section'>
              {isEdit && (
                <RerunNote data-testid='rerun-note'>
                  <strong>Note:</strong> Editing patient demographics or insurance information will re-run all completed
                  steps.
                </RerunNote>
              )}
              {isEdit && <Body>The AI fills these from the packet — edit them if needed.</Body>}
              <SectionTitle>Patient Demographics</SectionTitle>
              <Fields>{DEMOGRAPHIC_FIELDS.map(renderField)}</Fields>
              <SectionTitle>Insurance Information</SectionTitle>
              <Fields>{INSURANCE_FIELDS.map(renderField)}</Fields>
            </div>
          )}
        </div>

        <Actions>
          <Button variant='ghost' onClick={onClose} data-testid='cancel-referral'>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || isUploading}
            loading={submitting}
            data-testid='save-referral'>
            {isEdit ? 'Save' : 'Create Referral'}
          </Button>
        </Actions>
      </Form>
    </StandardModal>
  );
}

const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 16px;
`;

const Lead = styled(Body)`
  display: block;
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Field = styled.div`
  min-width: 200px;
  flex: 1;
`;

const SectionTitle = styled(BodySmall)`
  display: block;
  margin-top: 16px;
  font-weight: 700;
  color: ${colors.black75};
`;

const Optional = styled.span`
  font-weight: 400;
  color: ${colors.black50};
`;

const RerunNote = styled(BodySmall)`
  display: block;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.primaryBlue25};
  background: ${colors.primaryBlue05};
  color: ${colors.black75};
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.primaryBlue};
  font-weight: 600;
`;

const DropZone = styled.div`
  margin-top: 8px;
`;

const CurrentPacket = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.black75};
`;

const ReplaceWarning = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: #c0552f;
  font-weight: 700;
`;

const HelpText = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.accentRed};
`;
